import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateAdmin } from '../../Store/admin/adminSlice';
import { setCurrentUser } from '../../Store/user/userSlice';
import { avatarDisplayUrl } from '../../utils/imageUrl';
import { handleAvatarImgError, isPlaceholderAvatar } from '../../utils/defaultAvatar';
import styles from '../../styles/AdminPanel.module.scss';

const AdminProfile = ({ showMessage }) => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.user);
  const { isLoading } = useSelector((state) => state.admin);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    avatar: null,
    avatarPreview: '',
  });

  // Только при смене пользователя — не сбрасывать выбранный файл при обновлении currentUser
  useEffect(() => {
    if (!currentUser) return;
    setFormData({
      name: currentUser.name || '',
      email: currentUser.email || '',
      password: '',
      confirmPassword: '',
      avatar: null,
      avatarPreview: currentUser.avatar || '',
    });
  }, [currentUser?.id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'avatar' && files && files[0]) {
      const file = files[0];
      if (file.size > 5 * 1024 * 1024) {
        showMessage('error', 'Розмір зображення не повинен перевищувати 5 МБ');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          avatar: file,
          avatarPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showMessage('', '');

    // Валидация
    if (!formData.name || !formData.email) {
      showMessage('error', "Ім'я та email обов'язкові для заповнення");
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      showMessage('error', 'Пароли не совпадают');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      showMessage('error', 'Пароль повинен містити мінімум 6 символів');
      return;
    }

    try {
      const uploadData = new FormData();
      uploadData.append('name', formData.name);
      uploadData.append('email', formData.email);
      if (formData.password) {
        uploadData.append('password', formData.password);
      }

      const avatarInput = e.currentTarget.elements.namedItem('avatar');
      const avatarFile = avatarInput?.files?.[0] ?? null;
      const previousAvatar = currentUser?.avatar || '';
      const hadNewAvatar = Boolean(avatarFile);

      if (formData.avatarPreview?.startsWith('data:') && !avatarFile) {
        showMessage('error', 'Файл аватара втрачено. Оберіть зображення знову.');
        return;
      }

      if (avatarFile) {
        uploadData.append('avatar', avatarFile, avatarFile.name);
      }

      const updatedUser = await dispatch(updateAdmin(uploadData)).unwrap();

      if (hadNewAvatar) {
        const newAvatar = updatedUser?.avatar || '';
        const unchanged = !newAvatar
          || newAvatar === previousAvatar
          || isPlaceholderAvatar(newAvatar);
        if (unchanged) {
          showMessage('error', 'Аватар не збережено в базі. Перевірте права на php-api/storage та BASE_URL у .env');
          return;
        }
      }
      
      // Обновляем текущего пользователя в user slice
      dispatch(setCurrentUser({
        ...updatedUser,
        access_token: localStorage.getItem('token') // Сохраняем токен
      }));
      
      showMessage('success', 'Профіль успішно оновлено!');
      
      // Очищаем поля пароля
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: '',
        avatar: null,
        avatarPreview: updatedUser.avatar || prev.avatarPreview,
      }));
    } catch (error) {
      console.error('Помилка при оновленні профілю:', error);
      showMessage('error', typeof error === 'string' ? error : (error.response?.data?.message || 'Помилка при оновленні профілю'));
    }
  };

  return (
    <div className={styles.profilePage}>
      <div className={styles.profileForm}>
        <h2 className={styles.profileTitle}>Редагування профілю</h2>
        
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="avatar">Аватар</label>
            <div className={styles.avatarSection}>
              <div className={styles.avatarPreview}>
                <img
                  src={
                    formData.avatarPreview?.startsWith('data:')
                      ? formData.avatarPreview
                      : avatarDisplayUrl(formData.avatarPreview)
                  }
                  alt="Avatar preview"
                  className={styles.avatarImage}
                  onError={handleAvatarImgError}
                />
              </div>
              <input
                type="file"
                id="avatar"
                name="avatar"
                accept="image/*"
                onChange={handleChange}
                className={styles.fileInput}
              />
              <label htmlFor="avatar" className={styles.fileLabel}>
                Обрати зображення
              </label>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="name">Ім'я</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Введіть ім'я"
              required
              className={styles.formInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Введіть email"
              required
              className={styles.formInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password">Новий пароль (залиште порожнім, якщо не хочете змінювати)</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Введіть новий пароль"
              className={styles.formInput}
            />
          </div>

          {formData.password && (
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">Підтвердіть пароль</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Підтвердіть пароль"
                className={styles.formInput}
              />
            </div>
          )}

          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? 'Збереження...' : 'Зберегти зміни'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProfile;

